import type * as SchemaTypes from '../types';

import type { TranslatedStringDataFragment } from './TranslatedStringData';
import type { ApplicationMarketTypeFragment_DefaultApplicationMarket_Fragment, ApplicationMarketTypeFragment_NewZealandApplicationMarket_Fragment, ApplicationMarketTypeFragment_SingaporeApplicationMarket_Fragment } from './ApplicationMarketTypeFragment';
import type { DealerMarketDataFragment } from './DealerMarketData';
import type { BankDealerMarketDataFragment } from './BankDealerMarketData';
import type { NzFeesDealerMarketDataFragment } from './NzFeesDealerMarketData';
import type { DealerTranslatedStringSettingDataFragment } from './DealerTranslatedStringData';
import type { FinderApplicationModuleEmailContentSpecsFragment } from './FinderApplicationModuleEmailContentSpecs';
import type { TranslatedTextDataFragment } from './TranslationTextData';
import type { UploadFileWithPreviewFormDataFragment } from './UploadFileWithPreviewFormData';
import type { MyInfoSettingSpecFragment } from './MyInfoSettingSpec';
import type { AppointmentModuleSpecsFragment } from './AppointmentModuleSpecs';
import type { SimpleVersioningDataFragment } from './SimpleVersioningData';
import type { AuthorData_CorporateCustomer_Fragment, AuthorData_ExternalBank_Fragment, AuthorData_Guarantor_Fragment, AuthorData_LocalCustomer_Fragment, AuthorData_Salesforce_Fragment, AuthorData_SystemBank_Fragment, AuthorData_User_Fragment } from './AuthorData';
import type { AppointmentTimeSlotDataFragment } from './AppointmentTimeSlotData';
import type { NamirialSigningModuleSpecsFragment } from './NamirialSigningModuleSpecs';
import type { NamirialSettingsSpecFragment } from './NamirialSettingsSpec';
import type { AppointmentModuleEmailContentsSpecsFragment, AppointmentModuleEmailContentCustomerSpecsFragment, AppointmentModuleEmailContentSpecsFragment, AppointmentModuleEmailContentSalesPersonSpecsFragment, AppointmentModuleEmailContentFinderReservationSpecsFragment } from './AppointmentModuleEmailContentsSpecs';
import type { InsurerEntrypointContextDataFragment } from './InsurerEntrypointContextData';
import type { DealerDisclaimersConfiguratorDataFragment } from './DealerPriceDisclaimerConfiguratorData';
import { gql } from '@apollo/client';
import { TranslatedStringDataFragmentDoc } from './TranslatedStringData';
import { ApplicationMarketTypeFragmentFragmentDoc } from './ApplicationMarketTypeFragment';
import { DealerMarketDataFragmentDoc } from './DealerMarketData';
import { BankDealerMarketDataFragmentDoc } from './BankDealerMarketData';
import { NzFeesDealerMarketDataFragmentDoc } from './NzFeesDealerMarketData';
import { DealerTranslatedStringSettingDataFragmentDoc } from './DealerTranslatedStringData';
import { FinderApplicationModuleEmailContentSpecsFragmentDoc } from './FinderApplicationModuleEmailContentSpecs';
import { TranslatedTextDataFragmentDoc } from './TranslationTextData';
import { UploadFileWithPreviewFormDataFragmentDoc } from './UploadFileWithPreviewFormData';
import { MyInfoSettingSpecFragmentDoc } from './MyInfoSettingSpec';
import { AppointmentModuleSpecsFragmentDoc } from './AppointmentModuleSpecs';
import { SimpleVersioningDataFragmentDoc } from './SimpleVersioningData';
import { AuthorDataFragmentDoc } from './AuthorData';
import { AppointmentTimeSlotDataFragmentDoc } from './AppointmentTimeSlotData';
import { NamirialSigningModuleSpecsFragmentDoc } from './NamirialSigningModuleSpecs';
import { NamirialSettingsSpecFragmentDoc } from './NamirialSettingsSpec';
import { AppointmentModuleEmailContentsSpecsFragmentDoc, AppointmentModuleEmailContentCustomerSpecsFragmentDoc, AppointmentModuleEmailContentSpecsFragmentDoc, AppointmentModuleEmailContentSalesPersonSpecsFragmentDoc, AppointmentModuleEmailContentFinderReservationSpecsFragmentDoc } from './AppointmentModuleEmailContentsSpecs';
import { InsurerEntrypointContextDataFragmentDoc } from './InsurerEntrypointContextData';
import { DealerDisclaimersConfiguratorDataFragmentDoc } from './DealerPriceDisclaimerConfiguratorData';
export type EntrypointFinderApplicationPublicModuleFragment = (
  { __typename: 'FinderApplicationPublicModule' }
  & Pick<SchemaTypes.FinderApplicationPublicModule, 'id' | 'vehicleModuleId' | 'bankModuleId' | 'insuranceModuleId' | 'customerModuleId' | 'agreementsModuleId' | 'scenarios' | 'myInfoSettingId' | 'isOcrEnabled' | 'tradeIn' | 'isTradeInAmountVisible' | 'testDrive' | 'financingPreference' | 'isInsuranceOptional' | 'displayFinanceCalculator' | 'showInsuranceCalculator' | 'finderVehicleConditions' | 'depositPrice' | 'capModuleId' | 'isSearchCapCustomerOptional' | 'leadOrigin' | 'leadMedium' | 'leadCampaignId' | 'showVisitModelPageButton' | 'modelPageUrl' | 'market' | 'reservationPeriod' | 'paymentSettingsId' | 'showFromValueOnVehicleDetails' | 'bankDisplayPreference' | 'displayAppointmentDatepicker' | 'insurerDisplayPreference' | 'insurerIds'>
  & { vehicleModule: (
    { __typename: 'AdyenPaymentModule' }
    & Pick<SchemaTypes.AdyenPaymentModule, 'displayName'>
  ) | (
    { __typename: 'AppointmentModule' }
    & Pick<SchemaTypes.AppointmentModule, 'displayName'>
  ) | (
    { __typename: 'AudiCalculationModule' }
    & Pick<SchemaTypes.AudiCalculationModule, 'displayName'>
  ) | (
    { __typename: 'AudiFinancingModule' }
    & Pick<SchemaTypes.AudiFinancingModule, 'displayName'>
  ) | (
    { __typename: 'AudiSalesForceModule' }
    & Pick<SchemaTypes.AudiSalesForceModule, 'displayName'>
  ) | (
    { __typename: 'AutoplayModule' }
    & Pick<SchemaTypes.AutoplayModule, 'displayName'>
  ) | (
    { __typename: 'BankModule' }
    & Pick<SchemaTypes.BankModule, 'displayName'>
  ) | (
    { __typename: 'BasicSigningModule' }
    & Pick<SchemaTypes.BasicSigningModule, 'displayName'>
  ) | (
    { __typename: 'BmwModule' }
    & Pick<SchemaTypes.BmwModule, 'displayName'>
  ) | (
    { __typename: 'CapModule' }
    & Pick<SchemaTypes.CapModule, 'displayName'>
  ) | (
    { __typename: 'ConfiguratorModule' }
    & Pick<SchemaTypes.ConfiguratorModule, 'displayName'>
  ) | (
    { __typename: 'ConsentsAndDeclarationsModule' }
    & Pick<SchemaTypes.ConsentsAndDeclarationsModule, 'displayName'>
  ) | (
    { __typename: 'CtsModule' }
    & Pick<SchemaTypes.CtsModule, 'displayName'>
  ) | (
    { __typename: 'EventApplicationModule' }
    & Pick<SchemaTypes.EventApplicationModule, 'displayName'>
  ) | (
    { __typename: 'FinderApplicationPrivateModule' }
    & Pick<SchemaTypes.FinderApplicationPrivateModule, 'displayName'>
  ) | (
    { __typename: 'FinderApplicationPublicModule' }
    & Pick<SchemaTypes.FinderApplicationPublicModule, 'displayName'>
  ) | (
    { __typename: 'FinderVehicleManagementModule' }
    & Pick<SchemaTypes.FinderVehicleManagementModule, 'displayName'>
  ) | (
    { __typename: 'FiservPaymentModule' }
    & Pick<SchemaTypes.FiservPaymentModule, 'displayName'>
  ) | (
    { __typename: 'GiftVoucherModule' }
    & Pick<SchemaTypes.GiftVoucherModule, 'displayName'>
  ) | (
    { __typename: 'InsuranceModule' }
    & Pick<SchemaTypes.InsuranceModule, 'displayName'>
  ) | (
    { __typename: 'LabelsModule' }
    & Pick<SchemaTypes.LabelsModule, 'displayName'>
  ) | (
    { __typename: 'LocalCustomerManagementModule' }
    & Pick<SchemaTypes.LocalCustomerManagementModule, 'displayName'>
  ) | (
    { __typename: 'MaintenanceModule' }
    & Pick<SchemaTypes.MaintenanceModule, 'displayName'>
  ) | (
    { __typename: 'MobilityModule' }
    & Pick<SchemaTypes.MobilityModule, 'displayName'>
  ) | (
    { __typename: 'MyInfoModule' }
    & Pick<SchemaTypes.MyInfoModule, 'displayName'>
  ) | (
    { __typename: 'NamirialSigningModule' }
    & Pick<SchemaTypes.NamirialSigningModule, 'displayName'>
  ) | (
    { __typename: 'PayGatePaymentModule' }
    & Pick<SchemaTypes.PayGatePaymentModule, 'displayName'>
  ) | (
    { __typename: 'PorscheMasterDataModule' }
    & Pick<SchemaTypes.PorscheMasterDataModule, 'displayName'>
  ) | (
    { __typename: 'PorschePaymentModule' }
    & Pick<SchemaTypes.PorschePaymentModule, 'displayName'>
  ) | (
    { __typename: 'PromoCodeModule' }
    & Pick<SchemaTypes.PromoCodeModule, 'displayName'>
  ) | (
    { __typename: 'SdmModule' }
    & Pick<SchemaTypes.SdmModule, 'displayName'>
  ) | (
    { __typename: 'SimpleVehicleManagementModule' }
    & Pick<SchemaTypes.SimpleVehicleManagementModule, 'displayName'>
  ) | (
    { __typename: 'StandardApplicationModule' }
    & Pick<SchemaTypes.StandardApplicationModule, 'displayName'>
  ) | (
    { __typename: 'TradeInModule' }
    & Pick<SchemaTypes.TradeInModule, 'displayName'>
  ) | (
    { __typename: 'TtbPaymentModule' }
    & Pick<SchemaTypes.TtbPaymentModule, 'displayName'>
  ) | (
    { __typename: 'UserlikeChatbotModule' }
    & Pick<SchemaTypes.UserlikeChatbotModule, 'displayName'>
  ) | (
    { __typename: 'WebsiteModule' }
    & Pick<SchemaTypes.WebsiteModule, 'displayName'>
  ) | (
    { __typename: 'WhatsappLiveChatModule' }
    & Pick<SchemaTypes.WhatsappLiveChatModule, 'displayName'>
  ), porscheApprovedInfo: (
    { __typename: 'TranslatedString' }
    & TranslatedStringDataFragment
  ), company: (
    { __typename: 'Company' }
    & Pick<SchemaTypes.Company, 'id' | 'timeZone' | 'countryCode'>
  ), marketType: (
    { __typename: 'DefaultApplicationMarket' }
    & ApplicationMarketTypeFragment_DefaultApplicationMarket_Fragment
  ) | (
    { __typename: 'NewZealandApplicationMarket' }
    & ApplicationMarketTypeFragment_NewZealandApplicationMarket_Fragment
  ) | (
    { __typename: 'SingaporeApplicationMarket' }
    & ApplicationMarketTypeFragment_SingaporeApplicationMarket_Fragment
  ), priceDisclaimer: (
    { __typename: 'DealerDisclaimersConfigurator' }
    & { defaultValue: Array<SchemaTypes.Maybe<(
      { __typename: 'TranslatedString' }
      & TranslatedStringDataFragment
    )>>, overrides: Array<(
      { __typename: 'DealerDisclaimersOverridesConfigurator' }
      & Pick<SchemaTypes.DealerDisclaimersOverridesConfigurator, 'dealerId'>
      & { value: Array<(
        { __typename: 'TranslatedString' }
        & TranslatedStringDataFragment
      )> }
    )> }
  ), financingDisclaimer?: SchemaTypes.Maybe<(
    { __typename: 'DealerDisclaimersConfigurator' }
    & { defaultValue: Array<SchemaTypes.Maybe<(
      { __typename: 'TranslatedString' }
      & TranslatedStringDataFragment
    )>>, overrides: Array<(
      { __typename: 'DealerDisclaimersOverridesConfigurator' }
      & Pick<SchemaTypes.DealerDisclaimersOverridesConfigurator, 'dealerId'>
      & { value: Array<(
        { __typename: 'TranslatedString' }
        & TranslatedStringDataFragment
      )> }
    )> }
  )>, reservationInstructions?: SchemaTypes.Maybe<(
    { __typename: 'DealerTranslatedStringSetting' }
    & DealerTranslatedStringSettingDataFragment
  )>, emailContents: (
    { __typename: 'FinderApplicationModuleEmailContents' }
    & FinderApplicationModuleEmailContentSpecsFragment
  ), liveChatSetting?: SchemaTypes.Maybe<(
    { __typename: 'UserlikeChatbotSetting' }
    & Pick<SchemaTypes.UserlikeChatbotSetting, 'script' | 'id'>
  ) | (
    { __typename: 'WhatsappLiveChatSetting' }
    & Pick<SchemaTypes.WhatsappLiveChatSetting, 'link' | 'id'>
  )>, paymentSettings?: SchemaTypes.Maybe<(
    { __typename: 'AdyenPaymentSetting' }
    & Pick<SchemaTypes.AdyenPaymentSetting, 'id' | 'displayName' | 'paymentModuleId'>
  ) | (
    { __typename: 'FiservPaymentSetting' }
    & Pick<SchemaTypes.FiservPaymentSetting, 'id' | 'displayName' | 'paymentModuleId'>
  ) | (
    { __typename: 'PayGatePaymentSetting' }
    & Pick<SchemaTypes.PayGatePaymentSetting, 'id' | 'displayName' | 'paymentModuleId'>
  ) | (
    { __typename: 'PorschePaymentSetting' }
    & Pick<SchemaTypes.PorschePaymentSetting, 'id' | 'displayName' | 'paymentModuleId'>
  ) | (
    { __typename: 'TtbPaymentSetting' }
    & Pick<SchemaTypes.TtbPaymentSetting, 'id' | 'displayName' | 'paymentModuleId'>
  )>, myInfoSetting?: SchemaTypes.Maybe<(
    { __typename: 'MyInfoSetting' }
    & MyInfoSettingSpecFragment
  )>, promoCodeModule?: SchemaTypes.Maybe<(
    { __typename: 'AdyenPaymentModule' }
    & Pick<SchemaTypes.AdyenPaymentModule, 'id' | 'displayName'>
  ) | (
    { __typename: 'AppointmentModule' }
    & Pick<SchemaTypes.AppointmentModule, 'id' | 'displayName'>
  ) | (
    { __typename: 'AudiCalculationModule' }
    & Pick<SchemaTypes.AudiCalculationModule, 'id' | 'displayName'>
  ) | (
    { __typename: 'AudiFinancingModule' }
    & Pick<SchemaTypes.AudiFinancingModule, 'id' | 'displayName'>
  ) | (
    { __typename: 'AudiSalesForceModule' }
    & Pick<SchemaTypes.AudiSalesForceModule, 'id' | 'displayName'>
  ) | (
    { __typename: 'AutoplayModule' }
    & Pick<SchemaTypes.AutoplayModule, 'id' | 'displayName'>
  ) | (
    { __typename: 'BankModule' }
    & Pick<SchemaTypes.BankModule, 'id' | 'displayName'>
  ) | (
    { __typename: 'BasicSigningModule' }
    & Pick<SchemaTypes.BasicSigningModule, 'id' | 'displayName'>
  ) | (
    { __typename: 'BmwModule' }
    & Pick<SchemaTypes.BmwModule, 'id' | 'displayName'>
  ) | (
    { __typename: 'CapModule' }
    & Pick<SchemaTypes.CapModule, 'id' | 'displayName'>
  ) | (
    { __typename: 'ConfiguratorModule' }
    & Pick<SchemaTypes.ConfiguratorModule, 'id' | 'displayName'>
  ) | (
    { __typename: 'ConsentsAndDeclarationsModule' }
    & Pick<SchemaTypes.ConsentsAndDeclarationsModule, 'id' | 'displayName'>
  ) | (
    { __typename: 'CtsModule' }
    & Pick<SchemaTypes.CtsModule, 'id' | 'displayName'>
  ) | (
    { __typename: 'EventApplicationModule' }
    & Pick<SchemaTypes.EventApplicationModule, 'id' | 'displayName'>
  ) | (
    { __typename: 'FinderApplicationPrivateModule' }
    & Pick<SchemaTypes.FinderApplicationPrivateModule, 'id' | 'displayName'>
  ) | (
    { __typename: 'FinderApplicationPublicModule' }
    & Pick<SchemaTypes.FinderApplicationPublicModule, 'id' | 'displayName'>
  ) | (
    { __typename: 'FinderVehicleManagementModule' }
    & Pick<SchemaTypes.FinderVehicleManagementModule, 'id' | 'displayName'>
  ) | (
    { __typename: 'FiservPaymentModule' }
    & Pick<SchemaTypes.FiservPaymentModule, 'id' | 'displayName'>
  ) | (
    { __typename: 'GiftVoucherModule' }
    & Pick<SchemaTypes.GiftVoucherModule, 'id' | 'displayName'>
  ) | (
    { __typename: 'InsuranceModule' }
    & Pick<SchemaTypes.InsuranceModule, 'id' | 'displayName'>
  ) | (
    { __typename: 'LabelsModule' }
    & Pick<SchemaTypes.LabelsModule, 'id' | 'displayName'>
  ) | (
    { __typename: 'LocalCustomerManagementModule' }
    & Pick<SchemaTypes.LocalCustomerManagementModule, 'id' | 'displayName'>
  ) | (
    { __typename: 'MaintenanceModule' }
    & Pick<SchemaTypes.MaintenanceModule, 'id' | 'displayName'>
  ) | (
    { __typename: 'MobilityModule' }
    & Pick<SchemaTypes.MobilityModule, 'id' | 'displayName'>
  ) | (
    { __typename: 'MyInfoModule' }
    & Pick<SchemaTypes.MyInfoModule, 'id' | 'displayName'>
  ) | (
    { __typename: 'NamirialSigningModule' }
    & Pick<SchemaTypes.NamirialSigningModule, 'id' | 'displayName'>
  ) | (
    { __typename: 'PayGatePaymentModule' }
    & Pick<SchemaTypes.PayGatePaymentModule, 'id' | 'displayName'>
  ) | (
    { __typename: 'PorscheMasterDataModule' }
    & Pick<SchemaTypes.PorscheMasterDataModule, 'id' | 'displayName'>
  ) | (
    { __typename: 'PorschePaymentModule' }
    & Pick<SchemaTypes.PorschePaymentModule, 'id' | 'displayName'>
  ) | (
    { __typename: 'PromoCodeModule' }
    & Pick<SchemaTypes.PromoCodeModule, 'id' | 'displayName'>
  ) | (
    { __typename: 'SdmModule' }
    & Pick<SchemaTypes.SdmModule, 'id' | 'displayName'>
  ) | (
    { __typename: 'SimpleVehicleManagementModule' }
    & Pick<SchemaTypes.SimpleVehicleManagementModule, 'id' | 'displayName'>
  ) | (
    { __typename: 'StandardApplicationModule' }
    & Pick<SchemaTypes.StandardApplicationModule, 'id' | 'displayName'>
  ) | (
    { __typename: 'TradeInModule' }
    & Pick<SchemaTypes.TradeInModule, 'id' | 'displayName'>
  ) | (
    { __typename: 'TtbPaymentModule' }
    & Pick<SchemaTypes.TtbPaymentModule, 'id' | 'displayName'>
  ) | (
    { __typename: 'UserlikeChatbotModule' }
    & Pick<SchemaTypes.UserlikeChatbotModule, 'id' | 'displayName'>
  ) | (
    { __typename: 'WebsiteModule' }
    & Pick<SchemaTypes.WebsiteModule, 'id' | 'displayName'>
  ) | (
    { __typename: 'WhatsappLiveChatModule' }
    & Pick<SchemaTypes.WhatsappLiveChatModule, 'id' | 'displayName'>
  )>, appointmentModule?: SchemaTypes.Maybe<{ __typename: 'AdyenPaymentModule' } | (
    { __typename: 'AppointmentModule' }
    & AppointmentModuleSpecsFragment
  ) | { __typename: 'AudiCalculationModule' } | { __typename: 'AudiFinancingModule' } | { __typename: 'AudiSalesForceModule' } | { __typename: 'AutoplayModule' } | { __typename: 'BankModule' } | { __typename: 'BasicSigningModule' } | { __typename: 'BmwModule' } | { __typename: 'CapModule' } | { __typename: 'ConfiguratorModule' } | { __typename: 'ConsentsAndDeclarationsModule' } | { __typename: 'CtsModule' } | { __typename: 'EventApplicationModule' } | { __typename: 'FinderApplicationPrivateModule' } | { __typename: 'FinderApplicationPublicModule' } | { __typename: 'FinderVehicleManagementModule' } | { __typename: 'FiservPaymentModule' } | { __typename: 'GiftVoucherModule' } | { __typename: 'InsuranceModule' } | { __typename: 'LabelsModule' } | { __typename: 'LocalCustomerManagementModule' } | { __typename: 'MaintenanceModule' } | { __typename: 'MobilityModule' } | { __typename: 'MyInfoModule' } | { __typename: 'NamirialSigningModule' } | { __typename: 'PayGatePaymentModule' } | { __typename: 'PorscheMasterDataModule' } | { __typename: 'PorschePaymentModule' } | { __typename: 'PromoCodeModule' } | { __typename: 'SdmModule' } | { __typename: 'SimpleVehicleManagementModule' } | { __typename: 'StandardApplicationModule' } | { __typename: 'TradeInModule' } | { __typename: 'TtbPaymentModule' } | { __typename: 'UserlikeChatbotModule' } | { __typename: 'WebsiteModule' } | { __typename: 'WhatsappLiveChatModule' }>, insurers: Array<(
    { __typename: 'Insurer' }
    & InsurerEntrypointContextDataFragment
  )> }
);

export const EntrypointFinderApplicationPublicModuleFragmentDoc = /*#__PURE__*/ gql`
    fragment EntrypointFinderApplicationPublicModule on FinderApplicationPublicModule {
  __typename
  id
  vehicleModuleId
  vehicleModule {
    displayName
  }
  bankModuleId
  insuranceModuleId
  customerModuleId
  agreementsModuleId
  scenarios
  myInfoSettingId
  isOcrEnabled
  tradeIn
  isTradeInAmountVisible
  testDrive
  financingPreference
  isInsuranceOptional
  displayFinanceCalculator
  showInsuranceCalculator
  finderVehicleConditions
  porscheApprovedInfo {
    ...TranslatedStringData
  }
  depositPrice
  company {
    id
    timeZone
    countryCode
  }
  capModuleId
  isSearchCapCustomerOptional
  leadOrigin
  leadMedium
  leadCampaignId
  showVisitModelPageButton
  modelPageUrl
  market
  marketType {
    ...ApplicationMarketTypeFragment
  }
  priceDisclaimer {
    defaultValue {
      ...TranslatedStringData
    }
    overrides {
      dealerId
      value {
        ...TranslatedStringData
      }
    }
  }
  financingDisclaimer {
    defaultValue {
      ...TranslatedStringData
    }
    overrides {
      dealerId
      value {
        ...TranslatedStringData
      }
    }
  }
  reservationInstructions {
    ...DealerTranslatedStringSettingData
  }
  reservationPeriod
  emailContents {
    ...FinderApplicationModuleEmailContentSpecs
  }
  liveChatSetting {
    id
    ... on WhatsappLiveChatSetting {
      link
    }
    ... on UserlikeChatbotSetting {
      script
    }
  }
  paymentSettingsId
  paymentSettings {
    id
    displayName
    paymentModuleId
  }
  myInfoSettingId
  myInfoSetting {
    ...MyInfoSettingSpec
  }
  promoCodeModule {
    id
    displayName
  }
  showFromValueOnVehicleDetails
  bankDisplayPreference
  appointmentModule {
    ...AppointmentModuleSpecs
  }
  displayAppointmentDatepicker
  insurerDisplayPreference
  insurerIds
  insurers {
    ...InsurerEntrypointContextData
  }
  paymentSettings {
    __typename
  }
}
    `;